/**
 * "New lead" form dialog. Minimal fields per spec section 6.4
 * (companyName + source required; everything else optional and
 * editable on the slide-over in M8).
 */

import { useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { useCreateLead } from '@/lib/api/sales';
import { INDUSTRY, INDUSTRY_LABELS, type CreateLeadInput, type Industry } from '@workspace/shared';

const SOURCES: CreateLeadInput['source'][] = [
  'KNOWN_CONTACT',
  'REFERRAL',
  'INBOUND',
  'OUTBOUND',
  'EVENT',
  'TENDER',
  'GRANT',
  'PROJECT',
];

export function NewLeadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createLead = useCreateLead();
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [source, setSource] = useState<CreateLeadInput['source']>('INBOUND');
  const [industry, setIndustry] = useState<Industry | ''>('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!companyName.trim() || createLead.isPending) return;
    await createLead.mutateAsync({
      companyName: companyName.trim(),
      contactName: contactName.trim() || undefined,
      contactPhone: contactPhone.trim() || undefined,
      source,
      industry: industry || undefined,
    });
    setCompanyName('');
    setContactName('');
    setContactPhone('');
    setSource('INBOUND');
    setIndustry('');
    onClose();
  }

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay aria-hidden className="fixed inset-0 z-40 bg-black/55" />
        <Dialog.Content
          aria-label="New lead"
          className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 border border-mx-grey-800 bg-mx-card p-mx-5"
        >
          <div className="flex items-center justify-between">
            <Dialog.Title className="font-display text-mx-h2 font-semibold text-mx-white">
              New lead
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="flex h-7 w-7 items-center justify-center text-mx-grey-mid hover:text-mx-white"
            >
              <XMarkIcon className="h-5 w-5" strokeWidth={1.6} aria-hidden />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            Create a new lead with company and source.
          </Dialog.Description>
          <form className="mt-mx-4 flex flex-col gap-mx-3" onSubmit={onSubmit}>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">COMPANY NAME</span>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">CONTACT</span>
              <input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">CONTACT NUMBER</span>
              <input
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                type="tel"
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">SOURCE</span>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as CreateLeadInput['source'])}
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">INDUSTRY</span>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value as Industry | '')}
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                <option value="">—</option>
                {INDUSTRY.map((i) => (
                  <option key={i} value={i}>
                    {INDUSTRY_LABELS[i]}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-mx-3 flex justify-end gap-mx-2">
              <button
                type="button"
                onClick={onClose}
                className="text-mx-mono text-mx-grey-mid hover:text-mx-white"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={!companyName.trim() || createLead.isPending}
                className="h-9 bg-mx-orange px-mx-3 text-mx-body-sm font-medium text-mx-white hover:bg-mx-orange-hover disabled:bg-mx-grey-mid"
              >
                {createLead.isPending ? 'Saving…' : 'Create lead'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
